import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessCustomerClub } from '../utils/customerClubAccess';
import { formatRial } from '../utils/formatMoneyFa';

const PAGE_SIZE = 50;
const EXPORT_LIMIT = 2000;
const EARLIEST = '1392/01/01';

const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

type VisitOp = 'gte' | 'lte' | 'eq' | 'gt' | 'lt';
type DaysOp = 'gte' | 'lte' | 'eq';
type WizardStep = 0 | 1 | 2 | 3;

interface ServiceOption {
  label: string;
  type?: string;
  count: number;
}

interface TypeOption {
  label: string;
  count: number;
}

interface ClubMember {
  user_id?: number | null;
  patient_info_id?: number | null;
  full_name: string;
  phone: string;
  national_id?: string | null;
  completed_count: number;
  last_appointment_date?: string | null;
  days_since_last?: number | null;
  top_services: string[];
  total_amount?: number | null;
}

interface ReportResponse {
  from_date: string;
  to_date: string;
  total: number;
  items: ClubMember[];
}

interface AppliedFilters {
  fromDate: string;
  toDate: string;
  visitEnabled: boolean;
  visitOp: VisitOp;
  visitCount: string;
  daysEnabled: boolean;
  daysOp: DaysOp;
  daysCount: string;
  selectedTypes: string[];
  selectedServices: string[];
  query: string;
}

const STEPS = [
  { key: 0 as const, title: 'بازه زمانی', hint: 'تاریخ شروع و پایان گزارش' },
  { key: 1 as const, title: 'خدمات', hint: 'نوع خدمت و خدمت‌های جزئی' },
  { key: 2 as const, title: 'فیلترها', hint: 'تعداد مراجعه و فاصله زمانی' },
  { key: 3 as const, title: 'نتیجه', hint: 'فهرست مشتریان' },
];

const VISIT_OPS: { key: VisitOp; label: string }[] = [
  { key: 'gte', label: 'بیشتر یا مساوی' },
  { key: 'lte', label: 'کمتر یا مساوی' },
  { key: 'eq', label: 'مساوی' },
  { key: 'gt', label: 'بیشتر از' },
  { key: 'lt', label: 'کمتر از' },
];

const DAYS_OPS: { key: DaysOp; label: string }[] = [
  { key: 'gte', label: 'حداقل' },
  { key: 'lte', label: 'حداکثر' },
  { key: 'eq', label: 'دقیقاً' },
];

const PRESETS: { label: string; months?: number; all?: boolean }[] = [
  { label: '۱۲ ماه', months: 12 },
  { label: '۲ سال', months: 24 },
  { label: '۳ سال', months: 36 },
  { label: '۵ سال', months: 60 },
  { label: 'از ۱۳۹۲', all: true },
];

const formatDaysSince = (days?: number | null): string => {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'امروز';
  return `${toPersianDigits(String(days))} روز پیش`;
};

const csvEscape = (value: string): string => {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
};

const todayJalali = (): string => moment().format('jYYYY/jMM/jDD');
const monthsAgoJalali = (months: number): string =>
  moment().subtract(months, 'jMonth').format('jYYYY/jMM/jDD');

const AdminCustomerClubBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>(0);

  const [fromDate, setFromDate] = useState(() => monthsAgoJalali(12));
  const [toDate, setToDate] = useState(() => todayJalali());
  const [visitOp, setVisitOp] = useState<VisitOp>('gte');
  const [visitCount, setVisitCount] = useState('1');
  const [visitEnabled, setVisitEnabled] = useState(false);
  const [daysOp, setDaysOp] = useState<DaysOp>('gte');
  const [daysCount, setDaysCount] = useState('90');
  const [daysEnabled, setDaysEnabled] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLoadedFor, setCatalogLoadedFor] = useState('');
  const [query, setQuery] = useState('');

  const [applied, setApplied] = useState<AppliedFilters | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [rangeLabel, setRangeLabel] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessCustomerClub()) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('admin_token') || '';
    return { Authorization: `Bearer ${token}` };
  }, []);

  const buildParams = useCallback((f: AppliedFilters, skip: number, limit: number) => {
    const params = new URLSearchParams({
      from_date: f.fromDate,
      to_date: f.toDate,
      skip: String(skip),
      limit: String(limit),
    });
    if (f.query.trim()) params.set('q', f.query.trim());
    if (f.visitEnabled && f.visitCount) {
      params.set('visit_op', f.visitOp);
      params.set('visit_count', String(Math.max(1, Number(f.visitCount) || 1)));
    }
    if (f.daysEnabled && f.daysCount !== '') {
      params.set('days_op', f.daysOp);
      params.set('days_count', String(Math.max(0, Number(f.daysCount) || 0)));
    }
    if (f.selectedServices.length) {
      params.set('services', f.selectedServices.join('|'));
    } else if (f.selectedTypes.length) {
      params.set('service_types', f.selectedTypes.join('|'));
    }
    return params;
  }, []);

  const fetchCatalog = useCallback(async () => {
    const key = `${fromDate}|${toDate}`;
    if (catalogLoadedFor === key && types.length) return;
    setCatalogLoading(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        limit: '200',
      });
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/services?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('خطا در دریافت فهرست خدمات');
      const data = await res.json();
      setTypes(Array.isArray(data.types) ? data.types : []);
      setServices(Array.isArray(data.services) ? data.services : []);
      setCatalogLoadedFor(key);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در فهرست خدمات');
    } finally {
      setCatalogLoading(false);
    }
  }, [authHeaders, catalogLoadedFor, fromDate, toDate, types.length]);

  const fetchReportPage = useCallback(
    async (filters: AppliedFilters, pageNum: number) => {
      setLoading(true);
      setError(null);
      try {
        const apiBaseUrl = getApiBaseUrl();
        const skip = (pageNum - 1) * PAGE_SIZE;
        const res = await fetch(
          `${apiBaseUrl}/admin/customer-club/report?${buildParams(filters, skip, PAGE_SIZE)}`,
          { headers: authHeaders() }
        );
        if (res.status === 401) {
          navigate('/admin/login');
          return;
        }
        if (res.status === 403) {
          navigate('/admin/dashboard');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت گزارش');
        }
        const data = (await res.json()) as ReportResponse;
        setTotal(Number(data.total) || 0);
        setMembers(Array.isArray(data.items) ? data.items : []);
        setRangeLabel({
          from: data.from_date || filters.fromDate,
          to: data.to_date || filters.toDate,
        });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'خطا در دریافت گزارش');
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, buildParams, navigate]
  );

  const runReport = (filters: AppliedFilters) => {
    setApplied(filters);
    setPage(1);
    setStep(3);
  };

  useEffect(() => {
    if (!applied || step !== 3) return;
    void fetchReportPage(applied, page);
  }, [applied, fetchReportPage, page, step]);

  const filteredServices = useMemo(() => {
    if (!selectedTypes.length) return services;
    return services.filter((s) => selectedTypes.includes(s.type || ''));
  }, [selectedTypes, services]);

  const toggleType = (label: string) => {
    setSelectedTypes((prev) => {
      const next = prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label];
      setSelectedServices((svcs) =>
        svcs.filter((svc) => {
          const row = services.find((s) => s.label === svc);
          if (!row?.type) return true;
          if (!next.length) return true;
          return next.includes(row.type);
        })
      );
      return next;
    });
  };

  const toggleService = (label: string) => {
    setSelectedServices((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const applyPreset = (preset: { months?: number; all?: boolean }) => {
    if (preset.all) {
      setFromDate(EARLIEST);
      setToDate(todayJalali());
    } else if (preset.months) {
      setFromDate(monthsAgoJalali(preset.months));
      setToDate(todayJalali());
    }
    setCatalogLoadedFor('');
  };

  const snapshotFilters = (): AppliedFilters => ({
    fromDate,
    toDate,
    visitEnabled,
    visitOp,
    visitCount,
    daysEnabled,
    daysOp,
    daysCount,
    selectedTypes: [...selectedTypes],
    selectedServices: [...selectedServices],
    query,
  });

  const goNext = async () => {
    setError(null);
    if (step === 0) {
      if (!fromDate || !toDate) {
        setError('بازه زمانی را مشخص کنید');
        return;
      }
      setStep(1);
      await fetchCatalog();
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      runReport(snapshotFilters());
    }
  };

  const goBack = () => {
    setError(null);
    if (step === 3) {
      setStep(2);
      return;
    }
    setStep((s) => Math.max(0, (s as number) - 1) as WizardStep);
  };

  const restart = () => {
    setStep(0);
    setApplied(null);
    setMembers([]);
    setTotal(0);
    setError(null);
  };

  const downloadCsv = async () => {
    if (!applied) return;
    setExporting(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiBaseUrl}/admin/customer-club/report?${buildParams(applied, 0, EXPORT_LIMIT)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('خطا در تهیه خروجی');
      const data = (await res.json()) as ReportResponse;
      const rows = data.items || [];
      const header = [
        'رتبه',
        'نام',
        'موبایل',
        'کد ملی',
        'پذیرش HIS',
        'مبلغ (ریال)',
        'آخرین پذیرش',
        'روز از آخرین مراجعه',
        'خدمات پرتکرار',
      ];
      const lines = [header.join(',')];
      rows.forEach((row, idx) => {
        lines.push(
          [
            String(idx + 1),
            csvEscape(row.full_name || ''),
            csvEscape(row.phone || ''),
            csvEscape(row.national_id || ''),
            String(row.completed_count ?? 0),
            row.total_amount == null ? '' : String(Math.round(row.total_amount)),
            csvEscape(row.last_appointment_date || ''),
            row.days_since_last == null ? '' : String(row.days_since_last),
            csvEscape((row.top_services || []).join('، ')),
          ].join(',')
        );
      });
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `گزارش_باشگاه_${applied.fromDate.replace(/\//g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در تهیه خروجی');
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canGoNext = step === 0 ? Boolean(fromDate && toDate) : step === 1 || step === 2;

  return (
    <div className="space-y-6 text-base text-gray-900" style={{ direction: 'rtl' }}>
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-slate-700 via-indigo-800 to-violet-800 px-5 py-5 text-white shadow-lg shadow-indigo-900/20 sm:px-6">
        <div
          className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">گزارش‌ساز باشگاه مشتریان</h1>
            <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-indigo-100/95">
              مرحله‌به‌مرحله فیلترها را بسازید؛ در پایان فهرست مشتریان نمایش داده می‌شود.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/customer-club')}
            className="shrink-0 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20"
          >
            سگمنت‌ها
          </button>
        </div>
      </section>

      <nav className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
        <ol className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {STEPS.map((s, idx) => {
            const active = step === s.key;
            const done = step > s.key;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  disabled={(s.key > step && !(s.key === 3 && applied)) || (s.key === 3 && !applied)}
                  onClick={() => {
                    if (s.key <= step || (s.key === 3 && applied)) setStep(s.key);
                  }}
                  className={`w-full rounded-xl border px-3 py-3 text-right transition disabled:cursor-default ${
                    active
                      ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200'
                      : done
                        ? 'border-emerald-200 bg-emerald-50/60'
                        : 'border-gray-100 bg-gray-50/80 opacity-80'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                        active
                          ? 'bg-indigo-600 text-white'
                          : done
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white text-gray-500 ring-1 ring-gray-200'
                      }`}
                    >
                      {done && !active ? '✓' : toPersianDigits(String(idx + 1))}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{s.title}</p>
                      <p className="text-[11px] text-gray-500">{s.hint}</p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {step === 0 && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">۱. بازه زمانی را انتخاب کنید</h2>
          <p className="mt-1 text-sm text-gray-500">فقط پذیرش‌های HIS داخل این بازه در گزارش می‌آیند.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p)}
                className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-1.5 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="mt-5 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500">از تاریخ</label>
              <PersianDatePicker
                value={fromDate}
                onChange={(v) => {
                  setFromDate(v);
                  setCatalogLoadedFor('');
                }}
                compact
                placement="above"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">تا تاریخ</label>
              <PersianDatePicker
                value={toDate}
                onChange={(v) => {
                  setToDate(v);
                  setCatalogLoadedFor('');
                }}
                compact
                placement="above"
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-400">
            قدیمی‌ترین داده قابل اعتماد: {toPersianDigits(EARLIEST)}
          </p>
        </section>
      )}

      {step === 1 && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">۲. نوع خدمت و خدمت‌ها</h2>
          <p className="mt-1 text-sm text-gray-500">
            اختیاری است. اگر خدمت جزئی انتخاب شود، فیلتر فقط همان خدمت اعمال می‌شود.
          </p>
          {catalogLoading ? (
            <LoadingSpinner text="در حال بارگذاری فهرست خدمات این بازه..." />
          ) : (
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-800">نوع خدمت</p>
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/80 p-2">
                  {types.length === 0 ? (
                    <p className="px-2 py-6 text-center text-xs text-gray-500">نوعی در این بازه نیست.</p>
                  ) : (
                    types.map((t) => (
                      <label
                        key={t.label}
                        className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white"
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(t.label)}
                            onChange={() => toggleType(t.label)}
                            className="rounded border-gray-300 text-indigo-600"
                          />
                          {t.label}
                        </span>
                        <span className="tabular-nums text-xs text-gray-400">
                          {t.count.toLocaleString('fa-IR')}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800">خدمت‌ها</p>
                  {selectedServices.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setSelectedServices([])}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      پاک کردن انتخاب
                    </button>
                  ) : null}
                </div>
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/80 p-2">
                  {filteredServices.length === 0 ? (
                    <p className="px-2 py-6 text-center text-xs text-gray-500">خدمتی نیست.</p>
                  ) : (
                    filteredServices.map((s) => (
                      <label
                        key={s.label}
                        className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedServices.includes(s.label)}
                            onChange={() => toggleService(s.label)}
                            className="shrink-0 rounded border-gray-300 text-indigo-600"
                          />
                          <span className="truncate">{s.label}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-xs text-gray-400">
                          {s.count.toLocaleString('fa-IR')}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">
            بازه فعال: {toPersianDigits(fromDate)} تا {toPersianDigits(toDate)}
            {selectedServices.length
              ? ` · ${toPersianDigits(String(selectedServices.length))} خدمت انتخاب‌شده`
              : selectedTypes.length
                ? ` · ${toPersianDigits(String(selectedTypes.length))} نوع انتخاب‌شده`
                : ' · بدون فیلتر خدمت'}
          </p>
        </section>
      )}

      {step === 2 && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">۳. فیلترهای تکمیلی</h2>
          <p className="mt-1 text-sm text-gray-500">همه اختیاری‌اند؛ می‌توانید مستقیم گزارش بسازید.</p>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-100 bg-slate-50/60 p-4">
              <label className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                <input
                  type="checkbox"
                  checked={visitEnabled}
                  onChange={(e) => setVisitEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600"
                />
                تعداد مراجعه
              </label>
              <div className={`flex flex-wrap gap-2 ${visitEnabled ? '' : 'opacity-45'}`}>
                <select
                  disabled={!visitEnabled}
                  value={visitOp}
                  onChange={(e) => setVisitOp(e.target.value as VisitOp)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {VISIT_OPS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  disabled={!visitEnabled}
                  type="number"
                  min={1}
                  value={visitCount}
                  onChange={(e) => setVisitCount(e.target.value)}
                  className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums"
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-slate-50/60 p-4">
              <label className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                <input
                  type="checkbox"
                  checked={daysEnabled}
                  onChange={(e) => setDaysEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600"
                />
                روز از آخرین مراجعه
              </label>
              <div className={`flex flex-wrap gap-2 ${daysEnabled ? '' : 'opacity-45'}`}>
                <select
                  disabled={!daysEnabled}
                  value={daysOp}
                  onChange={(e) => setDaysOp(e.target.value as DaysOp)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {DAYS_OPS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  disabled={!daysEnabled}
                  type="number"
                  min={0}
                  value={daysCount}
                  onChange={(e) => setDaysCount(e.target.value)}
                  className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums"
                />
                <span className="self-center text-sm text-gray-500">روز</span>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-1 block text-sm font-semibold text-gray-800">جستجو (اختیاری)</label>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="نام، موبایل یا کد ملی"
              className="w-full max-w-xl rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            />
          </div>

          <div className="mt-5 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-3 text-sm text-indigo-900">
            <p className="font-medium">خلاصه انتخاب‌ها</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-indigo-800/90">
              <li>
                بازه: {toPersianDigits(fromDate)} تا {toPersianDigits(toDate)}
              </li>
              <li>
                خدمت:{' '}
                {selectedServices.length
                  ? selectedServices.join('، ')
                  : selectedTypes.length
                    ? `همهٔ ${selectedTypes.join('، ')}`
                    : 'همه خدمات'}
              </li>
              <li>
                مراجعه:{' '}
                {visitEnabled
                  ? `${VISIT_OPS.find((o) => o.key === visitOp)?.label} ${toPersianDigits(visitCount)}`
                  : 'بدون محدودیت'}
              </li>
            </ul>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">فهرست مشتریان</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {toPersianDigits(String(total))} مشتری
                {rangeLabel.from
                  ? ` — ${toPersianDigits(rangeLabel.from)} تا ${toPersianDigits(rangeLabel.to)}`
                  : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={restart}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                گزارش جدید
              </button>
              <button
                type="button"
                disabled={exporting || total === 0}
                onClick={() => void downloadCsv()}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {exporting ? 'در حال خروجی…' : 'خروجی CSV'}
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner text="در حال دریافت فهرست مشتریان..." />
          ) : members.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-500">
              با این فیلترها مشتری‌ای یافت نشد.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" style={{ direction: 'rtl' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رتبه</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نام</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">موبایل</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">کد ملی</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">پذیرش</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">مبلغ</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">آخرین پذیرش</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">فاصله</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">خدمات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {members.map((row, idx) => (
                    <tr key={row.patient_info_id || `${row.national_id}-${idx}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500">
                        {toPersianDigits(String((page - 1) * PAGE_SIZE + idx + 1))}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {row.full_name}
                      </td>
                      <td
                        className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700"
                        dir="ltr"
                      >
                        {toPersianDigits(row.phone || '')}
                      </td>
                      <td
                        className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700"
                        dir="ltr"
                      >
                        {row.national_id ? toPersianDigits(row.national_id) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold tabular-nums text-gray-900">
                        {row.completed_count.toLocaleString('fa-IR')}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-900">
                        {row.total_amount != null ? formatRial(row.total_amount) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700">
                        {row.last_appointment_date
                          ? toPersianDigits(row.last_appointment_date)
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatDaysSince(row.days_since_last)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {(row.top_services || []).join('، ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && !loading && (
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-700 disabled:opacity-40"
              >
                قبلی
              </button>
              <span className="tabular-nums text-gray-600">
                صفحه {toPersianDigits(String(page))} از {toPersianDigits(String(pageCount))}
              </span>
              <button
                type="button"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-700 disabled:opacity-40"
              >
                بعدی
              </button>
            </div>
          )}
        </section>
      )}

      {step < 3 && (
        <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0 || loading}
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            مرحله قبل
          </button>
          <button
            type="button"
            disabled={!canGoNext || loading || (step === 1 && catalogLoading)}
            onClick={() => void goNext()}
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
          >
            {loading ? 'در حال ساخت…' : step === 2 ? 'نمایش فهرست مشتریان' : 'ادامه'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminCustomerClubBuilder;
